import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../hooks/useTheme';
import type { ActiveTourStep } from '../../types/learnerTour';
import { TourHelpIcon } from './TourHelpIconBadge';
import {
  computePopoverPlacement,
  isLearnerTourOverlayPageKey,
  releaseDocumentScrollLock,
  restoreLearnerPageScrollAfterTour,
  scrollOverlayTargetIntoView,
  scrollTargetIntoViewIfNeeded,
} from '../../utils/productTourPlacement';
import {
  dispatchTourTargetBlur,
  dispatchTourTargetFocus,
  dispatchTourPageEnd,
} from '../../learnerTour/tourTargetEvents';
import {
  canInstantSkipTourTarget,
  findResolvableStepIndex,
  measureTourHighlightRect,
  queryTourTargetElement,
  resolveTourTargetAfterFocus,
  tourTargetResolveDelayMs,
  TOUR_QUICK_SKIP_MS,
  TOUR_TARGET_RESOLVE_MS,
  type TourTargetResolveMode,
} from '../../learnerTour/tourTargetResolution';
import { FLOATING_KEYBOARD_ANIM_MS } from '../../utils/learnerSlideModalMotion';

export interface ProductTourOverlayProps {
  pageKey: string;
  steps: ActiveTourStep[];
  onComplete: (opts?: { disableAll?: boolean }) => void;
  onSkip: (opts?: { disableAll?: boolean }) => void;
  onDismiss: () => void;
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type OverlayPhase = 'entering' | 'shown' | 'exiting';
type SkipModalPhase = 'entering' | 'shown' | 'exiting';

const EXIT_MS = FLOATING_KEYBOARD_ANIM_MS;
const EST_SKIP_MODAL_HEIGHT_PX = 268;
const TOUR_VISIBLE_RATIO = 0.7;
const SKIP_MODAL_WIDTH_SCALE = 1.2;

export const ProductTourOverlay: React.FC<ProductTourOverlayProps> = ({
  pageKey,
  steps,
  onComplete,
  onSkip,
  onDismiss,
}) => {
  const { t } = useTranslation('tour');
  const { direction } = useAppLocale();
  const { isDark } = useTheme();
  const isRtl = direction === 'rtl';

  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState<OverlayPhase>('entering');
  const [skipModalPhase, setSkipModalPhase] = useState<SkipModalPhase | null>(null);
  const [highlight, setHighlight] = useState<HighlightRect | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const skipModalRef = useRef<HTMLDivElement>(null);
  const panelContentRef = useRef<HTMLDivElement>(null);
  const skipModalExitTimerRef = useRef<number | null>(null);
  const [skipModalPosition, setSkipModalPosition] = useState<{
    top: number;
    left: number;
    width: number;
    peek: 'top' | 'bottom';
  } | null>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  const panelHeightFrameRef = useRef<number | null>(null);
  const [placement, setPlacement] = useState<ReturnType<typeof computePopoverPlacement> | null>(null);
  const prevStepRef = useRef<{ pageKey: string; targetKey: string } | null>(null);
  const exitTimerRef = useRef<number | null>(null);
  const resolvingRef = useRef(false);
  const skipForwardRef = useRef(true);
  const stepIndexRef = useRef(0);
  const allowBackgroundScrollRef = useRef(false);

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex >= steps.length - 1;
  const progress = steps.length > 0 ? ((stepIndex + 1) / steps.length) * 100 : 0;

  stepIndexRef.current = stepIndex;

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('shown'));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(
    () => () => {
      if (exitTimerRef.current != null) window.clearTimeout(exitTimerRef.current);
      if (skipModalExitTimerRef.current != null) window.clearTimeout(skipModalExitTimerRef.current);
      releaseDocumentScrollLock();
    },
    []
  );

  useEffect(() => {
    const preventBackgroundScroll = (event: Event) => {
      if (allowBackgroundScrollRef.current) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popoverRef.current?.contains(target)) return;
      if (skipModalRef.current?.contains(target)) return;
      event.preventDefault();
    };
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false, capture: true });
    return () => {
      document.removeEventListener('wheel', preventBackgroundScroll, { capture: true });
      document.removeEventListener('touchmove', preventBackgroundScroll, { capture: true });
    };
  }, []);

  const blockBackgroundInteraction = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const dismissTour = useCallback(
    (callback: () => void) => {
      if (exitTimerRef.current != null) return;
      setPhase('exiting');
      exitTimerRef.current = window.setTimeout(() => {
        exitTimerRef.current = null;
        restoreLearnerPageScrollAfterTour(pageKey);
        callback();
      }, EXIT_MS);
    },
    [pageKey]
  );

  const applyHighlightForTarget = useCallback((targetKey: string, keepPreviousOnMiss = false) => {
    const el = queryTourTargetElement(pageKey, targetKey);
    if (!el) {
      if (!keepPreviousOnMiss) {
        setHighlight(null);
        setPlacement(null);
      }
      return false;
    }
    // Modal overlay tours live in fixed panels — window scrollIntoView would yank the page behind them.
    if (isLearnerTourOverlayPageKey(pageKey)) {
      scrollOverlayTargetIntoView(el);
    } else {
      allowBackgroundScrollRef.current = true;
      scrollTargetIntoViewIfNeeded(el, 'auto');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          allowBackgroundScrollRef.current = false;
        });
      });
    }
    setHighlight(measureTourHighlightRect(el));
    return true;
  }, [pageKey]);

  const measure = useCallback(() => {
    if (!step) return;
    applyHighlightForTarget(step.targetKey, resolvingRef.current);
  }, [applyHighlightForTarget, step]);

  const completeTour = useCallback(
    (opts?: { disableAll?: boolean }) => {
      if (step) dispatchTourTargetBlur(pageKey, step.targetKey);
      dispatchTourPageEnd(pageKey);
      dismissTour(() => onComplete(opts));
    },
    [dismissTour, onComplete, pageKey, step]
  );

  const skipTour = useCallback(
    (opts?: { disableAll?: boolean }) => {
      if (step) dispatchTourTargetBlur(pageKey, step.targetKey);
      dispatchTourPageEnd(pageKey);
      dismissTour(() => onSkip(opts));
    },
    [dismissTour, onSkip, pageKey, step]
  );

  const computeSkipModalPosition = useCallback((modalHeight: number) => {
    const pop = popoverRef.current;
    if (!pop || modalHeight <= 0) return null;

    const popRect = pop.getBoundingClientRect();
    const modalWidth = popRect.width * SKIP_MODAL_WIDTH_SCALE;
    const visibleHeight = popRect.height * TOUR_VISIBLE_RATIO;
    const overlapHeight = popRect.height - visibleHeight;
    const popCenterY = popRect.top + popRect.height / 2;
    const placeBelow = popCenterY < window.innerHeight / 2;
    const peek: 'top' | 'bottom' = placeBelow ? 'top' : 'bottom';

    let top = placeBelow
      ? popRect.top + visibleHeight
      : popRect.top + overlapHeight - modalHeight;

    const left = Math.max(
      8,
      Math.min(
        popRect.left + popRect.width / 2 - modalWidth / 2,
        window.innerWidth - modalWidth - 8
      )
    );
    top = Math.max(8, Math.min(top, window.innerHeight - modalHeight - 8));

    return { top, left, width: modalWidth, peek };
  }, []);

  const openSkipModal = useCallback(() => {
    if (skipModalExitTimerRef.current != null) {
      window.clearTimeout(skipModalExitTimerRef.current);
      skipModalExitTimerRef.current = null;
    }
    const initial = computeSkipModalPosition(EST_SKIP_MODAL_HEIGHT_PX);
    if (initial) setSkipModalPosition(initial);
    setSkipModalPhase('entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setSkipModalPhase('shown'));
    });
  }, [computeSkipModalPosition]);

  const syncSkipModalPosition = useCallback(() => {
    const modal = skipModalRef.current;
    if (!modal) return;
    const modalHeight = modal.getBoundingClientRect().height || modal.offsetHeight;
    const next = computeSkipModalPosition(modalHeight);
    if (next) setSkipModalPosition(next);
  }, [computeSkipModalPosition]);

  const closeSkipModal = useCallback((then?: () => void) => {
    if (skipModalPhase === 'exiting' || !skipModalPhase) {
      then?.();
      return;
    }
    setSkipModalPhase('exiting');
    skipModalExitTimerRef.current = window.setTimeout(() => {
      skipModalExitTimerRef.current = null;
      setSkipModalPhase(null);
      setSkipModalPosition(null);
      then?.();
    }, EXIT_MS);
  }, [skipModalPhase]);

  const handleSkipPage = useCallback(() => {
    closeSkipModal(() => skipTour());
  }, [closeSkipModal, skipTour]);

  const handleDisableAllPages = useCallback(() => {
    closeSkipModal(() => skipTour({ disableAll: true }));
  }, [closeSkipModal, skipTour]);

  const dismissTourQuietly = useCallback(() => {
    if (step) dispatchTourTargetBlur(pageKey, step.targetKey);
    dispatchTourPageEnd(pageKey);
    dismissTour(onDismiss);
  }, [dismissTour, onDismiss, pageKey, step]);

  const resolveStepTarget = useCallback(
    async (index: number, mode: TourTargetResolveMode = 'patient'): Promise<boolean> => {
      const targetKey = steps[index]?.targetKey;
      if (!targetKey) return false;
      if (canInstantSkipTourTarget(targetKey)) return false;

      const immediate = queryTourTargetElement(pageKey, targetKey);
      if (immediate) return true;

      const resolveMs = tourTargetResolveDelayMs(targetKey, mode);
      await resolveTourTargetAfterFocus(pageKey, targetKey, resolveMs);
      return queryTourTargetElement(pageKey, targetKey) != null;
    },
    [pageKey, steps]
  );

  const findNextResolvableIndex = useCallback(
    async (fromIndex: number, direction: 1 | -1): Promise<number | null> => {
      return findResolvableStepIndex(pageKey, steps, fromIndex, direction);
    },
    [pageKey, steps]
  );

  const transitionToStepIndex = useCallback(
    async (nextIndex: number, searchDirection: 1 | -1) => {
      if (nextIndex < 0 || nextIndex >= steps.length) {
        completeTour();
        return;
      }
      if (nextIndex === stepIndexRef.current) {
        measure();
        return;
      }

      resolvingRef.current = true;
      let candidate = nextIndex;

      while (candidate >= 0 && candidate < steps.length) {
        const targetKey = steps[candidate]?.targetKey ?? '';
        if (canInstantSkipTourTarget(targetKey)) {
          candidate += searchDirection;
          continue;
        }
        const ok = await resolveStepTarget(candidate, 'patient');
        if (ok) {
          resolvingRef.current = false;
          setStepIndex(candidate);
          return;
        }
        candidate += searchDirection;
      }

      resolvingRef.current = false;
      if (searchDirection === -1 && nextIndex > 0) {
        const forward = await findNextResolvableIndex(nextIndex + 1, 1);
        if (forward != null) {
          setStepIndex(forward);
          return;
        }
      }
      completeTour();
    },
    [completeTour, findNextResolvableIndex, measure, resolveStepTarget, steps.length]
  );

  useEffect(() => {
    if (steps.length === 0) return undefined;
    let cancelled = false;

    void (async () => {
      resolvingRef.current = true;
      const first = await findNextResolvableIndex(0, 1);
      if (cancelled) return;
      resolvingRef.current = false;
      if (first == null) {
        completeTour();
        return;
      }
      if (first !== 0) setStepIndex(first);
    })();

    return () => {
      cancelled = true;
      resolvingRef.current = false;
    };
  }, [completeTour, findNextResolvableIndex, pageKey, steps]);

  useLayoutEffect(() => {
    if (!step) return undefined;

    const prev = prevStepRef.current;
    dispatchTourTargetFocus(pageKey, step.targetKey);
    if (prev && (prev.targetKey !== step.targetKey || prev.pageKey !== pageKey)) {
      dispatchTourTargetBlur(prev.pageKey, prev.targetKey, step.targetKey);
    }
    prevStepRef.current = { pageKey, targetKey: step.targetKey };

    applyHighlightForTarget(step.targetKey, true);
    const rafId = requestAnimationFrame(() => {
      applyHighlightForTarget(step.targetKey, true);
      window.setTimeout(() => applyHighlightForTarget(step.targetKey, true), 320);
    });
    const tId = window.setTimeout(() => applyHighlightForTarget(step.targetKey, false), 720);
    const tId2 = window.setTimeout(() => applyHighlightForTarget(step.targetKey, false), 1200);
    const tipLateMs = tourTargetResolveDelayMs(step.targetKey) > TOUR_TARGET_RESOLVE_MS ? 2200 : 0;
    const tId3 = tipLateMs
      ? window.setTimeout(() => applyHighlightForTarget(step.targetKey, false), tipLateMs)
      : undefined;
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(tId);
      window.clearTimeout(tId2);
      if (tId3 != null) window.clearTimeout(tId3);
    };
  }, [applyHighlightForTarget, stepIndex, step, pageKey]);

  useEffect(() => {
    return () => {
      const prev = prevStepRef.current;
      if (prev) dispatchTourTargetBlur(prev.pageKey, prev.targetKey);
      restoreLearnerPageScrollAfterTour(pageKey);
    };
  }, [pageKey]);

  useEffect(() => {
    if (!step || resolvingRef.current || phase !== 'shown') return undefined;

    let cancelled = false;
    const waitMs = canInstantSkipTourTarget(step.targetKey)
      ? 0
      : tourTargetResolveDelayMs(step.targetKey, 'patient') + TOUR_QUICK_SKIP_MS;

    const tId = window.setTimeout(() => {
      void (async () => {
        if (cancelled || resolvingRef.current) return;
        if (queryTourTargetElement(pageKey, step.targetKey)) return;

        if (canInstantSkipTourTarget(step.targetKey)) {
          if (stepIndexRef.current >= steps.length - 1) {
            completeTour();
            return;
          }
          void transitionToStepIndex(stepIndexRef.current + 1, 1);
          return;
        }

        const ok = await resolveStepTarget(stepIndexRef.current, 'patient');
        if (cancelled || resolvingRef.current) return;
        if (ok || queryTourTargetElement(pageKey, step.targetKey)) return;

        if (stepIndexRef.current >= steps.length - 1) {
          completeTour();
          return;
        }
        void transitionToStepIndex(stepIndexRef.current + 1, 1);
      })();
    }, waitMs);

    return () => {
      cancelled = true;
      window.clearTimeout(tId);
    };
  }, [
    completeTour,
    pageKey,
    phase,
    resolveStepTarget,
    step,
    steps.length,
    transitionToStepIndex,
  ]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [measure]);

  useLayoutEffect(() => {
    if (phase === 'exiting') return undefined;
    const el = panelContentRef.current;
    if (!el) return undefined;

    const syncPanelHeight = () => {
      if (panelHeightFrameRef.current != null) cancelAnimationFrame(panelHeightFrameRef.current);
      panelHeightFrameRef.current = requestAnimationFrame(() => {
        panelHeightFrameRef.current = null;
        setPanelHeight(el.offsetHeight);
      });
    };

    syncPanelHeight();
    const ro = new ResizeObserver(syncPanelHeight);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (panelHeightFrameRef.current != null) cancelAnimationFrame(panelHeightFrameRef.current);
    };
  }, [stepIndex, step?.body, step?.title, phase]);

  useLayoutEffect(() => {
    if (phase === 'exiting') return undefined;
    if (!highlight || !popoverRef.current || !step) {
      setPlacement(null);
      return undefined;
    }
    const popEl = popoverRef.current;
    const recomputePlacement = () => {
      const pop = popEl.getBoundingClientRect();
      const pref = (step.popoverSide ?? 'auto') as 'auto' | 'top' | 'bottom' | 'left' | 'right';
      setPlacement(
        computePopoverPlacement(
          new DOMRect(highlight.left + 6, highlight.top + 6, highlight.width - 12, highlight.height - 12),
          pop.width || 320,
          pop.height || 180,
          pref,
          isRtl
        )
      );
    };
    recomputePlacement();
    const ro = new ResizeObserver(recomputePlacement);
    ro.observe(popEl);
    return () => ro.disconnect();
  }, [highlight, step, isRtl, panelHeight, phase]);

  useLayoutEffect(() => {
    if (!skipModalPhase) return undefined;

    const sync = () => syncSkipModalPosition();
    sync();
    const rafId = requestAnimationFrame(sync);
    const ro = skipModalRef.current ? new ResizeObserver(sync) : null;
    if (skipModalRef.current) ro?.observe(skipModalRef.current);
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      cancelAnimationFrame(rafId);
      ro?.disconnect();
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [skipModalPhase, panelHeight, placement, syncSkipModalPosition]);

  const goNext = () => {
    skipForwardRef.current = true;
    if (isLast) {
      completeTour();
      return;
    }
    void transitionToStepIndex(stepIndex + 1, 1);
  };

  const goPrev = () => {
    if (isFirst) return;
    skipForwardRef.current = false;
    void transitionToStepIndex(stepIndex - 1, -1);
  };

  const handleSkip = () => openSkipModal();

  const skipModalVisible = skipModalPhase === 'shown' || skipModalPhase === 'exiting';
  const skipModalExiting = skipModalPhase === 'exiting';
  const skipModalOpen = skipModalPhase != null;
  const skipModalDimmed = skipModalPhase === 'shown' || skipModalPhase === 'exiting';

  if (!step || steps.length === 0) return null;

  const dimAlpha = isDark ? 0.62 : 0.72;
  const panelVisible = phase === 'shown';
  const isAnimatingOut = phase === 'exiting';

  const popoverPositionStyle: React.CSSProperties = placement
    ? {
        top: placement.top,
        left: placement.left,
        ['--tour-arrow-offset' as string]: `${placement.arrowOffset}px`,
      }
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  const overlay = (
    <div
      className="product-tour-root fixed inset-0 z-[200] pointer-events-none"
      dir={direction}
      role="dialog"
      aria-modal="true"
      aria-label={t('dialogAriaLabel')}
    >
      <div
        className={`product-tour-interaction-shield fixed inset-0 z-[201] touch-none${
          skipModalOpen ? ' pointer-events-none' : ' pointer-events-auto'
        }${isAnimatingOut ? ' pointer-events-none' : ''}`}
        aria-hidden
        onClick={blockBackgroundInteraction}
        onPointerDown={blockBackgroundInteraction}
        onWheel={blockBackgroundInteraction}
        onTouchMove={blockBackgroundInteraction}
      />

      <div
        className={`product-tour-backdrop-layer keyboard-slide-modal-backdrop pointer-events-none fixed inset-0 z-[200] ${
          panelVisible ? 'is-visible' : ''
        }`}
      >
        {highlight ? (
          <div
            className="product-tour-spotlight fixed transition-all duration-300 ease-out"
            style={{
              top: highlight.top,
              left: highlight.left,
              width: highlight.width,
              height: highlight.height,
              boxShadow: `0 0 0 9999px rgba(0, 0, 0, ${dimAlpha})`,
            }}
          />
        ) : (
          <div
            className="product-tour-backdrop-fill fixed inset-0"
            style={{ backgroundColor: `rgba(0, 0, 0, ${dimAlpha})` }}
          />
        )}
      </div>

      <div
        ref={popoverRef}
        className={`product-tour-popover pointer-events-auto fixed z-[202] w-[min(64vw,15.5rem)] sm:w-[min(92vw,22rem)]${
          skipModalDimmed ? ' product-tour-popover--dimmed' : ''
        } ${isDark ? 'product-tour-popover--dark' : 'product-tour-popover--light'}${isAnimatingOut ? ' pointer-events-none' : ''}`}
        style={popoverPositionStyle}
      >
        <div
          data-side={placement?.side ?? 'bottom'}
          className={`product-tour-popover__anim keyboard-slide-modal-panel keyboard-slide-modal-panel--slide-up overflow-visible ${
            panelVisible ? 'is-visible' : ''
          }`}
        >
          <div
            className="product-tour-popover__panel relative z-[1] overflow-hidden rounded-2xl"
            style={panelHeight != null ? { height: panelHeight } : undefined}
          >
            <div ref={panelContentRef}>
              <div className="relative px-4 pb-2 pe-11 pt-3">
                <button
                  type="button"
                  onClick={dismissTourQuietly}
                  className="product-tour-popover__close absolute top-2.5 end-2.5 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label={t('dismissTour')}
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="mb-2">
                  <p className="product-tour-popover__step-label text-xs font-semibold uppercase tracking-wide text-teal-600 dark:text-teal-400">
                    {t('stepOf', { current: stepIndex + 1, total: steps.length })}
                  </p>
                  <h3 className="product-tour-popover__title mt-0.5 text-base font-semibold leading-snug">
                    {step.title}
                  </h3>
                </div>
              </div>

              <div className="product-tour-popover__body-wrap mx-3 mb-3 rounded-xl px-3 py-2.5">
                <div
                  className="product-tour-body text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                  dangerouslySetInnerHTML={{ __html: step.body }}
                />
              </div>

              <div className="px-4 pb-3">
                <div className="h-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div
                    className="h-full rounded-full bg-teal-500 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 border-t border-slate-200/80 px-4 py-3 dark:border-slate-700/80">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="product-tour-popover__skip font-medium text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  {t('skip')}
                </button>
                <div className="flex items-center gap-2">
                  {!isFirst && (
                    <button
                      type="button"
                      onClick={goPrev}
                      className={`product-tour-popover__action inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                        isDark
                          ? 'border-slate-600 hover:bg-slate-800'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {isRtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                      {t('previous')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={goNext}
                    className="product-tour-popover__action inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-teal-700"
                  >
                    {isLast ? t('finish') : t('next')}
                    {!isLast &&
                      (isRtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {skipModalOpen && (
        <div className="product-tour-skip-layer fixed inset-0 z-[203] pointer-events-none">
          <div
            className={`product-tour-skip-backdrop fixed inset-0 pointer-events-auto${
              skipModalVisible ? ' is-visible' : ''
            }${skipModalExiting ? ' is-exiting' : ''}`}
            aria-hidden
            onClick={() => closeSkipModal()}
          />
          <div className="product-tour-skip-modal-wrap fixed inset-0 pointer-events-none">
            <div
              ref={skipModalRef}
              role="dialog"
              aria-modal="true"
              aria-label={t('skipModalTitle')}
              data-peek={skipModalPosition?.peek ?? 'top'}
              className={`product-tour-skip-modal pointer-events-auto fixed overflow-hidden rounded-2xl ${
                isDark ? 'product-tour-skip-modal--dark' : 'product-tour-skip-modal--light'
              }${skipModalPhase === 'shown' ? ' is-visible' : ''}${skipModalExiting ? ' is-exiting' : ''}`}
              style={
                skipModalPosition
                  ? {
                      top: skipModalPosition.top,
                      left: skipModalPosition.left,
                      width: skipModalPosition.width,
                    }
                  : undefined
              }
            >
              <div className="relative flex items-start justify-between gap-2 border-b border-slate-200/80 px-4 py-3 pe-11 dark:border-slate-700/80">
                <h3 className="product-tour-popover__title min-w-0 flex-1 text-base font-semibold leading-snug">
                  {t('skipModalTitle')}
                </h3>
                <button
                  type="button"
                  onClick={() => closeSkipModal()}
                  className="product-tour-popover__close absolute top-2.5 end-2.5 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label={t('skipModalClose')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="product-tour-skip-options flex flex-col gap-3 px-4 py-3">
                <button
                  type="button"
                  onClick={handleSkipPage}
                  className="product-tour-skip-option product-tour-skip-option--page rounded-xl border-2 px-3 py-3 text-start transition hover:border-teal-400 hover:bg-teal-50/90 dark:hover:border-teal-500/70 dark:hover:bg-teal-950/40"
                >
                  <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {t('skipThisPageOption')}
                  </span>
                  <span className="product-tour-skip-option__hint mt-1 block text-xs leading-snug text-slate-500 dark:text-slate-400">
                    {t('skipThisPageHint')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleDisableAllPages}
                  className="product-tour-skip-option product-tour-skip-option--global rounded-xl border-2 px-3 py-3 text-start transition hover:border-amber-400 hover:bg-amber-50/90 dark:hover:border-amber-500/60 dark:hover:bg-amber-950/30"
                >
                  <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {t('disableAllPagesOption')}
                  </span>
                  <span className="product-tour-skip-option__hint mt-1 block text-xs leading-snug text-slate-500 dark:text-slate-400">
                    {t('disableAllPagesHint')}
                  </span>
                </button>
              </div>

              <div className="border-t border-slate-200/80 px-4 py-3 dark:border-slate-700/80">
                <p className="product-tour-skip-modal__tour-hint text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                  <Trans
                    i18nKey="skipModalTourHint"
                    ns="tour"
                    components={{ tourIcon: <TourHelpIcon /> }}
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
};
